import { useEffect, useRef } from "react";
import {
  buildBaseProperties,
  setAnalyticsPersonProperties,
} from "../integrations/analytics/client";
import type { AnalyticsProperties } from "../integrations/analytics/sanitize";
import { useOwnerContext } from "../hooks/useOwnerContext";
import type { useSubscription } from "../hooks/useSubscription";

type Props = {
  /**
   * The shell's own subscription state for the resolved owner. Passed in rather than read with a
   * second `useSubscription` instance, which would add its own polling and realtime channel.
   */
  subscription: ReturnType<typeof useSubscription>;
};

/**
 * Keeps the signed-in person's PostHog profile in sync with account facts that only exist
 * once the app has loaded them — role, partner link, subscription state, build.
 *
 * `identify` runs off the auth session alone (see App.tsx), so without this a person profile
 * carries little more than an id and an email, and none of it is useful for segmenting.
 *
 * Health traits are intentionally NOT sent: tracking goal (TTC/avoid/pregnant), postpartum
 * status, charting methods and enabled optional chart fields all reveal health information,
 * so they stay in our database and never reach PostHog. The analytics sanitizer also drops
 * those keys as a backstop (see BLOCKED_KEY_PATTERN in sanitize.ts).
 *
 * Owner-derived traits are only written when the signed-in person *is* the owner: a spouse's
 * profile must not be stamped with the owner's setup, and an instructor's never with a client's.
 */
const AnalyticsPersonProfileSync = ({ subscription }: Props) => {
  const ownerContext = useOwnerContext().data;
  const isReady = ownerContext?.status === "ready";
  const isOwner =
    isReady &&
    ownerContext.viewingClientUserId === null &&
    ownerContext.actorUserId === ownerContext.couple.id;
  const ownerUserId = isOwner ? ownerContext.couple.id : undefined;
  const lastSentSignature = useRef<string | null>(null);

  useEffect(() => {
    const status = ownerContext?.status;
    // Anonymous visitors have no account facts to describe, and writing these before
    // `identify` would attach them to a throwaway per-browser person instead.
    if (!status || status === "unauthenticated") {
      return;
    }

    const baseProperties = buildBaseProperties();
    const couple = isOwner && ownerContext.status === "ready" ? ownerContext.couple : null;
    const accountRole =
      ownerContext.status !== "ready"
        ? null
        : ownerContext.actorRole === "instructor"
          ? "instructor"
          : ownerContext.actorUserId === ownerContext.couple.id
            ? "owner"
            : "partner";

    const properties: AnalyticsProperties = {
      ...baseProperties,
      onboarding_status: status,
      has_completed_onboarding: status !== "needs-onboarding",
      account_role: accountRole,
      actor_role: ownerContext.status === "ready" ? ownerContext.actorRole : null,
      ...(couple && ownerContext.status === "ready"
        ? {
            has_partner: Boolean(couple.partner_user_id),
            timezone: couple.timezone ?? null,
            can_write_observations: ownerContext.canWriteObservations,
          }
        : {}),
      // A still-loading subscription reads as "no data" rather than as a real status, so it
      // is held back until the hook has actually resolved something.
      ...(ownerUserId && !subscription.loading
        ? {
            subscription_status: subscription.status,
            subscription_has_access: subscription.hasAccess,
            subscription_days_remaining: subscription.daysRemaining,
            subscription_in_grace_period: subscription.isGracePeriod,
            subscription_plan_key: subscription.planKey,
            subscription_billing_provider: subscription.billingProvider,
            subscription_auto_renews: subscription.subscriptionAutoRenews,
            subscription_free_for_life: subscription.freeForLife,
            trial_end_date: subscription.trialEndDate,
            subscription_end_date: subscription.subscriptionEndDate,
            subscription_renewal_date: subscription.subscriptionRenewalDate,
            app_store_environment: subscription.appStoreEnvironment,
          }
        : {}),
    };

    // Every setPersonProperties call sends a `$set` event, and the subscription hook repolls
    // once a minute — so only an actual change in the profile is worth a request.
    // Keyed by actor too, so a second account signing in on the same browser re-sends even
    // when its profile happens to look identical.
    const signature = JSON.stringify([
      ownerContext.status === "ready" ? ownerContext.actorUserId : status,
      properties,
    ]);
    if (signature === lastSentSignature.current) {
      return;
    }

    lastSentSignature.current = signature;
    setAnalyticsPersonProperties(properties, {
      first_seen_platform: baseProperties.client_platform,
      first_seen_release: baseProperties.app_release,
    });
  }, [isOwner, ownerContext, ownerUserId, subscription]);

  return null;
};

export default AnalyticsPersonProfileSync;
